import { Injectable, inject, signal } from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ToastContainerUIComponent } from './toast-container.component';
import { ToastModel, ToastOptions, ToastType } from '../../../types/toast.type';
import {
  TOAST_DEFAULT_TIMEOUT,
  TOAST_EXTENDED_TIMEOUT,
  TOAST_MAX_OPENED,
} from '../../../constants/toast.constants';

/**
 * Bottom-right toast stack, the replacement for `ngx-toastr` (archived upstream, pinned to
 * Angular 21 peers). Behaviour kept from the old `ConfigService.toastrConfig`: at most
 * `TOAST_MAX_OPENED` on screen, identical toasts deduplicated with their timer restarted,
 * dismissal only through the close button, and HTML allowed in the message.
 */
@Injectable({ providedIn: 'root' })
export class ToastService {
  readonly toasts = signal<readonly ToastModel[]>([]);

  private readonly overlay = inject(Overlay);

  private overlayRef?: OverlayRef;
  private nextId = 0;
  private readonly timers = new Map<number, ReturnType<typeof setTimeout>>();

  success(message: string, title = '', options?: ToastOptions): void {
    this.show('success', message, title, options);
  }

  error(message: string, title = '', options?: ToastOptions): void {
    this.show('error', message, title, options);
  }

  warning(message: string, title = '', options?: ToastOptions): void {
    this.show('warning', message, title, options);
  }

  info(message: string, title = '', options?: ToastOptions): void {
    this.show('info', message, title, options);
  }

  show(type: ToastType, message: string, title = '', options?: ToastOptions): void {
    const timeOut = options?.timeOut ?? TOAST_DEFAULT_TIMEOUT;

    const duplicate = this.toasts().find(
      (toast) => toast.type === type && toast.title === title && toast.message === message,
    );
    if (duplicate) {
      this.startTimer(duplicate.id, duplicate.timeOut ?? timeOut);
      return;
    }

    const toast: ToastModel = { id: this.nextId++, type, title, message, timeOut };

    this.attachOverlay();
    this.toasts.update((toasts) => [...toasts, toast].slice(-TOAST_MAX_OPENED));
    // A toast pushed out by the cap keeps a live timer otherwise.
    this.clearOrphanedTimers();
    this.startTimer(toast.id, timeOut);
  }

  dismiss(id: number): void {
    this.clearTimer(id);
    this.toasts.update((toasts) => toasts.filter((toast) => toast.id !== id));

    if (!this.toasts().length) {
      this.overlayRef?.detach();
    }
  }

  /** Freezes the countdown while the pointer rests on a toast. */
  holdTimer(id: number): void {
    this.clearTimer(id);
  }

  releaseTimer(id: number): void {
    this.startTimer(id, TOAST_EXTENDED_TIMEOUT);
  }

  private startTimer(id: number, delay: number): void {
    this.clearTimer(id);
    this.timers.set(
      id,
      setTimeout(() => this.dismiss(id), delay),
    );
  }

  private clearTimer(id: number): void {
    const timer = this.timers.get(id);
    if (timer !== undefined) {
      clearTimeout(timer);
      this.timers.delete(id);
    }
  }

  private clearOrphanedTimers(): void {
    const live = new Set(this.toasts().map((toast) => toast.id));
    for (const id of [...this.timers.keys()]) {
      if (!live.has(id)) {
        this.clearTimer(id);
      }
    }
  }

  private attachOverlay(): void {
    this.overlayRef ??= this.overlay.create({
      hasBackdrop: false,
      scrollStrategy: this.overlay.scrollStrategies.noop(),
      positionStrategy: this.overlay.position().global().bottom('20px').right('20px'),
    });

    if (!this.overlayRef.hasAttached()) {
      this.overlayRef.attach(new ComponentPortal(ToastContainerUIComponent));
    }
  }
}
